/**
 * MonthlyStatsBarChart Component
 *
 * Displays monthly statistics as a grouped bar chart with two bars per month.
 * Based on the BarChartCard pattern but customized for monthly stats.
 *
 * Design specs:
 * - Card: 628px x 459px
 * - Background: #15203C
 * - Border radius: 20px
 * - Padding: 30px
 * - Bar width: 23px
 * - Bar gap: 5px
 * - Group gap: 24px
 * - Colors: #7846FF (completos), #45169C (pendentes)
 * - Chart height: 286px
 * - Legend: 7px circles
 */

import { MonthlyStats } from "@/types/gestao-recursos.interface";
import { BarChart, Bar, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Skeleton } from "@/components/ui/skeleton";

interface MonthlyStatsBarChartProps {
  data: MonthlyStats[];
  period?: string;
  isLoading?: boolean;
}

export function MonthlyStatsBarChart({
  data,
  period = "Últimos 7 dias",
  isLoading,
}: MonthlyStatsBarChartProps) {
  return (
    <div className="bg-[#15203C] rounded-[20px] p-[30px] w-[628px] h-[459px] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-[10px]">
        <h3 className="text-[16px] font-semibold text-[#F6F6F6]">
          Estatísticas por mês
        </h3>
        <p className="text-[12px] text-[#888888]">{period}</p>
      </div>

      {/* Chart */}
      <div className="flex-1 min-h-[200px]">
        {isLoading ? (
          <Skeleton className="h-full" />
        ) : data.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-400 text-sm">
            Sem dados disponíveis
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%" minHeight={200}>
            <BarChart
              data={data}
              barGap={5}
              barCategoryGap={24}
              margin={{ top: 20, right: 0, left: 0, bottom: 20 }}
            >
              <XAxis
                dataKey="month"
                tick={{ fill: "#FFFFFF", fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: "#FFFFFF", fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(value) => {
                  if (value >= 100000) return `R$${value / 1000}k`;
                  if (value >= 1000) return `R$${value / 1000}k`;
                  return `R$${value}`;
                }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#15203C",
                  border: "1px solid #5D5D5D",
                  borderRadius: "8px",
                }}
                labelStyle={{ color: "#F6F6F6" }}
                itemStyle={{ color: "#F6F6F6" }}
                formatter={(value: number) => [
                  new Intl.NumberFormat("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  }).format(value),
                  "",
                ]}
              />
              <Bar
                dataKey="completos"
                fill="#7846FF"
                radius={[4, 4, 0, 0]}
                barSize={23}
              />
              <Bar
                dataKey="pendentes"
                fill="#45169C"
                radius={[4, 4, 0, 0]}
                barSize={23}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-6 mt-3">
        <div className="flex items-center gap-[10px]">
          <div className="size-[7px] rounded-full bg-[#7846FF]" />
          <p className="text-[12px] text-white">Completos</p>
        </div>
        <div className="flex items-center gap-[10px]">
          <div className="size-[7px] rounded-full bg-[#45169C]" />
          <p className="text-[12px] text-white">Pendentes</p>
        </div>
      </div>
    </div>
  );
}
